/**
 * Map Asset Manager
 * 
 * Manages loading, instancing, and placement of static map assets
 * like trees, rocks, buildings, etc. Supports efficient instancing
 * and automatic collision generation from GLB models.
 * 
 * Features:
 * - GLB model loading with automatic bounding box computation
 * - Compound collision support for rigged models with parts
 * - Debug visualization of collision shapes
 * - Per-mesh and per-part collision filtering
 * 
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/physics/compounds
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Color3 } from "@babylonjs/core/Maths/math.color";

// Import GLTF loader
import "@babylonjs/loaders/glTF";

import { IDisposable } from "../core/Engine.types";
import {
    MapAssetDefinition,
    MapAssetPlacement,
    MapAssetInstance,
    MapAssetBatchConfig,
    MapAssetEvent,
    MapAssetEventHandler,
    MapAssetEventType,
    CollisionMode,
} from "./MapAsset.types";
import {
    ComputedBounds,
    MeshPartBounds,
    computeBoundsFromMeshes,
    computeBoundsPerPart,
    findMainCollisionPart,
    createCollisionMeshFromBounds,
    createDebugCollisionMesh,
    createMeshWireframeDebug,
    collisionModeToPhysicsShape,
    printBoundsInfo,
    printMeshHierarchy,
    suggestCollisionShape,
} from "./BoundingBoxUtils";
import { TerrainManager } from "../terrain/index";

/**
 * Cached asset template data
 */
interface CachedAssetTemplate {
    definition: MapAssetDefinition;
    meshes: AbstractMesh[];
    bounds: ComputedBounds;
    /** Bounds computed for each individual mesh part */
    partBounds: MeshPartBounds[];
    instanceCount: number;
}

/**
 * MapAssetManager - Manages static map objects with collision
 */
export class MapAssetManager implements IDisposable {
    private _scene: Scene;
    private _basePath: string;
    private _assetDefinitions: Map<string, MapAssetDefinition> = new Map();
    private _assetCache: Map<string, CachedAssetTemplate> = new Map();
    private _instances: Map<string, MapAssetInstance> = new Map();
    private _eventHandlers: Map<MapAssetEventType, Set<MapAssetEventHandler>> = new Map();
    private _terrain: TerrainManager | null = null;
    private _physicsEnabled: boolean = true;
    private _debugMode: boolean = false;
    private _loadingAssets: Map<string, Promise<void>> = new Map();

    /**
     * Get all registered asset definitions
     */
    public get assetDefinitions(): MapAssetDefinition[] {
        return Array.from(this._assetDefinitions.values());
    }

    /**
     * Get all active instances
     */
    public get instances(): MapAssetInstance[] {
        return Array.from(this._instances.values());
    }

    /**
     * Get instance count
     */
    public get instanceCount(): number {
        return this._instances.size;
    }

    /**
     * Check if debug mode is enabled
     */
    public get debugMode(): boolean {
        return this._debugMode;
    }

    constructor(scene: Scene, basePath: string = "assets/") {
        this._scene = scene;
        this._basePath = basePath;
    }

    /**
     * Enable/disable global debug visualization
     */
    public setDebugMode(enabled: boolean): void {
        this._debugMode = enabled;
        console.log(`MapAssetManager: Debug mode ${enabled ? 'enabled' : 'disabled'}`);
        
        // Update visibility of existing debug meshes, or create them if they don't exist
        for (const instance of this._instances.values()) {
            if (instance.debugMeshes) {
                // Show/hide existing debug meshes
                instance.debugMeshes.forEach(mesh => mesh.setEnabled(enabled));
            } else if (enabled) {
                // Create debug meshes on-demand if they don't exist
                const cached = this._assetCache.get(instance.assetId);
                if (cached && cached.definition.collision?.mode !== CollisionMode.NONE) {
                    const totalScale = (cached.definition.scale ?? 1) * (instance.scale ?? 1);
                    const debugMeshes = this._createDebugVisualization(
                        instance.rootNode,
                        instance.meshes,
                        cached.bounds,
                        cached.definition,
                        totalScale,
                        instance.id
                    );
                    if (debugMeshes.length > 0) {
                        instance.debugMeshes = debugMeshes;
                        // Ensure they're visible since we just enabled debug mode
                        debugMeshes.forEach(mesh => mesh.setEnabled(true));
                    }
                }
            }
        }
    }

    /**
     * Set terrain manager for height adjustment
     */
    public setTerrain(terrain: TerrainManager): void {
        this._terrain = terrain;
    }

    /**
     * Enable or disable physics for new instances
     */
    public setPhysicsEnabled(enabled: boolean): void {
        this._physicsEnabled = enabled;
    }

    /**
     * Register a map asset definition
     */
    public registerAsset(definition: MapAssetDefinition): void {
        this._assetDefinitions.set(definition.id, definition);
        console.log(`MapAssetManager: Registered asset "${definition.id}"`);
    }

    /**
     * Register multiple assets at once
     */
    public registerAssets(definitions: MapAssetDefinition[]): void {
        definitions.forEach(def => this.registerAsset(def));
    }

    /**
     * Preload an asset (load and cache without placing)
     */
    public async preloadAsset(assetId: string): Promise<ComputedBounds> {
        const definition = this._assetDefinitions.get(assetId);
        if (!definition) {
            throw new Error(`MapAssetManager: Unknown asset "${assetId}"`);
        }

        // Check if already loading
        if (this._loadingAssets.has(assetId)) {
            await this._loadingAssets.get(assetId);
            return this._assetCache.get(assetId)!.bounds;
        }

        // Check cache
        if (this._assetCache.has(assetId)) {
            return this._assetCache.get(assetId)!.bounds;
        }

        // Load the asset
        const loadPromise = this._loadAsset(definition);
        this._loadingAssets.set(assetId, loadPromise);

        try {
            await loadPromise;
            this._emitEvent("asset_loaded", { assetId });
            return this._assetCache.get(assetId)!.bounds;
        } finally {
            this._loadingAssets.delete(assetId);
        }
    }

    /**
     * Internal asset loading
     */
    private async _loadAsset(definition: MapAssetDefinition): Promise<void> {
        const fullPath = this._basePath + definition.modelPath;
        const directory = fullPath.substring(0, fullPath.lastIndexOf("/") + 1);
        const filename = fullPath.substring(fullPath.lastIndexOf("/") + 1);

        console.log(`MapAssetManager: Loading "${definition.id}" from ${fullPath}`);

        const result = await SceneLoader.ImportMeshAsync(
            "",
            directory,
            filename,
            this._scene
        );

        // Hide template meshes (don't apply scale here - do it at placement time)
        result.meshes.forEach(mesh => {
            mesh.setEnabled(false); // Hide template meshes
        });

        // Force bounding info refresh
        result.meshes.forEach(mesh => {
            if (mesh.getTotalVertices() > 0) {
                mesh.computeWorldMatrix(true);
                mesh.refreshBoundingInfo({ applySkeleton: true });
            }
        });

        // Compute bounds from UNSCALED meshes - we'll apply scale at placement time
        const bounds = computeBoundsFromMeshes(result.meshes, true);
        
        // Compute per-part bounds for compound collision support
        const partBounds = computeBoundsPerPart(result.meshes);
        
        // Log bounds info for debugging
        printBoundsInfo(bounds, definition.id);
        
        // Print mesh hierarchy if in debug mode
        if (this._debugMode || definition.collision?.debugVisualize) {
            printMeshHierarchy(result.meshes, definition.id);
            console.log(`MapAssetManager: Found ${partBounds.length} collidable parts for "${definition.id}":`);
            partBounds.forEach(part => {
                console.log(`  - ${part.name}: ${part.isMainPart ? '(MAIN) ' : ''}shape=${PhysicsShapeType[part.suggestedShape]}`);
            });
        }

        // Suggest best collision shape
        const suggestion = suggestCollisionShape(bounds);
        console.log(`MapAssetManager: Collision suggestion for "${definition.id}": ${suggestion.recommended} - ${suggestion.reason}`);

        // Cache the template
        this._assetCache.set(definition.id, {
            definition,
            meshes: result.meshes,
            bounds,
            partBounds,
            instanceCount: 0,
        });

        console.log(`MapAssetManager: Asset "${definition.id}" loaded with ${result.meshes.length} meshes`);
    }

    /**
     * Place a single map asset instance
     */
    public async placeAsset(placement: MapAssetPlacement): Promise<MapAssetInstance> {
        // Ensure asset is loaded
        await this.preloadAsset(placement.assetId);

        const cached = this._assetCache.get(placement.assetId)!;
        const definition = cached.definition;
        cached.instanceCount++;

        // Generate instance ID
        const instanceId = placement.instanceId ?? 
            `${placement.assetId}_${cached.instanceCount}_${Date.now()}`;

        // Create root transform node
        const rootNode = new TransformNode(instanceId, this._scene);

        // Calculate total scale: definition.scale * per-instance scale variation
        const definitionScale = cached.definition.scale ?? 1;
        const instanceScale = placement.scale ?? 1;
        const totalScale = definitionScale * instanceScale;
        
        // Set initial position at terrain level
        let position = placement.position.clone();
        rootNode.position = position;

        // Apply rotation
        if (placement.rotation) {
            rootNode.rotationQuaternion = placement.rotation.clone();
        } else if (placement.randomYRotation) {
            const randomY = Math.random() * Math.PI * 2;
            rootNode.rotationQuaternion = Quaternion.FromEulerAngles(0, randomY, 0);
        }

        // Apply scale to root node - this handles ALL scaling (definition + instance)
        rootNode.scaling = new Vector3(totalScale, totalScale, totalScale);

        // Clone meshes for this instance
        const instanceMeshes: AbstractMesh[] = [];
        for (const templateMesh of cached.meshes) {
            if (templateMesh.getTotalVertices() === 0) continue;

            const clone = templateMesh.clone(`${instanceId}_${templateMesh.name}`, rootNode);
            if (clone) {
                clone.setEnabled(true);
                
                // Apply shadow settings
                if (definition.castShadows !== false) {
                    // Shadow casting is set per-mesh elsewhere
                }
                if (definition.receiveShadows !== false) {
                    clone.receiveShadows = true;
                }
                
                instanceMeshes.push(clone);
            }
        }

        // Now adjust position so model's bottom sits on terrain
        // Compute actual world bounds of the placed meshes
        if (instanceMeshes.length > 0) {
            // Force world matrix update
            rootNode.computeWorldMatrix(true);
            instanceMeshes.forEach(m => m.computeWorldMatrix(true));
            
            // Find the lowest Y point across all meshes
            let minWorldY = Infinity;
            for (const mesh of instanceMeshes) {
                mesh.refreshBoundingInfo({ applySkeleton: false });
                const boundingInfo = mesh.getBoundingInfo();
                const minY = boundingInfo.boundingBox.minimumWorld.y;
                if (minY < minWorldY) {
                    minWorldY = minY;
                }
            }
            
            // Adjust root position so minWorldY equals terrain height
            const terrainY = placement.position.y;
            const adjustment = terrainY - minWorldY;
            rootNode.position.y += adjustment;
            position = rootNode.position.clone();
            
            // Debug: log first placement
            if (cached.instanceCount <= 1) {
                console.log(`MapAssetManager: Placing "${placement.assetId}"`);
                console.log(`  totalScale=${totalScale.toFixed(3)}, minWorldY=${minWorldY.toFixed(3)}, terrainY=${terrainY.toFixed(3)}`);
                console.log(`  adjustment=${adjustment.toFixed(3)}, final position.y=${rootNode.position.y.toFixed(3)}`);
            }
        }

        // Setup physics collision
        let physicsAggregate: PhysicsAggregate | null = null;
        let compoundAggregates: PhysicsAggregate[] = [];
        let debugMeshes: Mesh[] = [];
        
        if (this._physicsEnabled && definition.collision?.mode !== CollisionMode.NONE) {
            const physicsResult = await this._setupPhysics(
                rootNode,
                instanceMeshes,
                cached.bounds,
                cached.partBounds,
                definition,
                totalScale, // Use totalScale instead of instanceScale
                instanceId
            );
            
            if (physicsResult) {
                physicsAggregate = physicsResult.primary;
                compoundAggregates = physicsResult.compound;
            }
        }
        
        // Create debug visualization if enabled
        const showDebug = this._debugMode || definition.collision?.debugVisualize;
        if (showDebug && definition.collision?.mode !== CollisionMode.NONE) {
            debugMeshes = this._createDebugVisualization(
                rootNode,
                instanceMeshes,
                cached.bounds,
                definition,
                totalScale, // Use totalScale instead of instanceScale
                instanceId
            );
        }

        // Create instance record
        const instance: MapAssetInstance = {
            id: instanceId,
            assetId: placement.assetId,
            rootNode,
            meshes: instanceMeshes,
            physicsAggregate,
            compoundAggregates: compoundAggregates.length > 0 ? compoundAggregates : undefined,
            debugMeshes: debugMeshes.length > 0 ? debugMeshes : undefined,
            position,
            scale: instanceScale,
            enabled: true,
        };

        this._instances.set(instanceId, instance);
        this._emitEvent("instance_created", { assetId: placement.assetId, instanceId });

        return instance;
    }

    /**
     * Setup physics collision for an instance
     * Supports both simple single-body and compound multi-part collision
     */
    private async _setupPhysics(
        rootNode: TransformNode,
        meshes: AbstractMesh[],
        bounds: ComputedBounds,
        partBounds: MeshPartBounds[],
        definition: MapAssetDefinition,
        scale: number,
        instanceId: string
    ): Promise<{ primary: PhysicsAggregate | null; compound: PhysicsAggregate[] } | null> {
        const collisionConfig = definition.collision;
        if (!collisionConfig) return null;
        
        const result = {
            primary: null as PhysicsAggregate | null,
            compound: [] as PhysicsAggregate[],
        };

        // Check if compound collision is enabled
        if (collisionConfig.useCompoundCollision && partBounds.length > 0) {
            return this._setupCompoundPhysics(
                rootNode,
                meshes,
                partBounds,
                definition,
                scale,
                instanceId
            );
        }

        // Standard single-body collision
        let shapeType: PhysicsShapeType;
        let collisionMesh: Mesh;

        if (collisionConfig.mode === CollisionMode.CUSTOM && collisionConfig.customDimensions) {
            // Use custom dimensions
            const custom = collisionConfig.customDimensions;
            shapeType = custom.shapeType;
            
            // Create collision mesh with custom dimensions
            collisionMesh = this._createCustomCollisionMesh(
                custom,
                scale,
                instanceId
            );
            collisionMesh.parent = rootNode;
        } else if (collisionConfig.mode === CollisionMode.MESH) {
            // Use the first mesh with vertices for mesh collision
            const meshForCollision = meshes.find(m => m.getTotalVertices() > 0);
            if (!meshForCollision) return null;
            
            shapeType = PhysicsShapeType.MESH;
            collisionMesh = meshForCollision as Mesh;
        } else if (collisionConfig.mode === CollisionMode.CONVEX_HULL) {
            // Use convex hull on the main mesh
            const meshForCollision = meshes.find(m => m.getTotalVertices() > 0);
            if (!meshForCollision) return null;
            
            shapeType = PhysicsShapeType.CONVEX_HULL;
            collisionMesh = meshForCollision as Mesh;
        } else {
            // Generate collision shape from bounds
            shapeType = collisionModeToPhysicsShape(collisionConfig.mode);
            
            // Scale bounds
            const scaledBounds: ComputedBounds = {
                ...bounds,
                size: bounds.size.scale(scale),
                halfExtents: bounds.halfExtents.scale(scale),
                center: bounds.center.scale(scale),
                sphereRadius: bounds.sphereRadius * scale,
                capsuleRadius: bounds.capsuleRadius * scale,
                capsuleHeight: bounds.capsuleHeight * scale,
            };
            
            collisionMesh = createCollisionMeshFromBounds(
                this._scene,
                scaledBounds,
                shapeType,
                `${instanceId}_collision`
            );
            collisionMesh.parent = rootNode;
            
            // Adjust collision mesh position relative to root
            collisionMesh.position = scaledBounds.center.subtract(rootNode.position);
            // Actually, position it at local center
            collisionMesh.position = new Vector3(0, scaledBounds.halfExtents.y, 0);
        }

        try {
            // Force world matrix computation to ensure correct positions for raycasting
            collisionMesh.computeWorldMatrix(true);
            
            // Create physics aggregate (static - mass = 0)
            const aggregate = new PhysicsAggregate(
                collisionMesh,
                shapeType,
                {
                    mass: 0, // Static body
                    friction: collisionConfig.friction ?? 0.5,
                    restitution: collisionConfig.restitution ?? 0.1,
                },
                this._scene
            );

            // Debug: Log collision mesh world position
            const worldPos = collisionMesh.getAbsolutePosition();
            console.log(`MapAssetManager: Created physics for "${instanceId}" at world pos (${worldPos.x.toFixed(2)}, ${worldPos.y.toFixed(2)}, ${worldPos.z.toFixed(2)})`);

            result.primary = aggregate;
            return result;
        } catch (error) {
            console.warn(`MapAssetManager: Failed to create physics for "${instanceId}":`, error);
            return null;
        }
    }
    
    /**
     * Setup compound physics collision using per-mesh parts
     * Creates separate collision bodies for each mesh part
     */
    private async _setupCompoundPhysics(
        rootNode: TransformNode,
        _meshes: AbstractMesh[],  // Reserved for future per-mesh collision
        partBounds: MeshPartBounds[],
        definition: MapAssetDefinition,
        scale: number,
        instanceId: string
    ): Promise<{ primary: PhysicsAggregate | null; compound: PhysicsAggregate[] }> {
        const collisionConfig = definition.collision!;
        const result = {
            primary: null as PhysicsAggregate | null,
            compound: [] as PhysicsAggregate[],
        };
        
        // Filter parts if a filter pattern is specified
        let partsToUse = partBounds;
        if (collisionConfig.collisionMeshFilter) {
            const filterRegex = new RegExp(collisionConfig.collisionMeshFilter, 'i');
            partsToUse = partBounds.filter(part => filterRegex.test(part.name));
            console.log(`MapAssetManager: Filtered ${partBounds.length} parts to ${partsToUse.length} using pattern "${collisionConfig.collisionMeshFilter}"`);
        }
        
        // Find the main part for the primary aggregate
        const mainPart = findMainCollisionPart(partsToUse);
        
        for (let i = 0; i < partsToUse.length; i++) {
            const part = partsToUse[i];
            const isMain = part === mainPart;
            
            // Scale the bounds
            const scaledBounds: ComputedBounds = {
                ...part.bounds,
                min: part.bounds.min.scale(scale),
                max: part.bounds.max.scale(scale),
                size: part.bounds.size.scale(scale),
                halfExtents: part.bounds.halfExtents.scale(scale),
                center: part.bounds.center.scale(scale),
                sphereRadius: part.bounds.sphereRadius * scale,
                capsuleRadius: part.bounds.capsuleRadius * scale,
                capsuleHeight: part.bounds.capsuleHeight * scale,
            };
            
            // Create collision mesh for this part
            const collisionMesh = createCollisionMeshFromBounds(
                this._scene,
                scaledBounds,
                part.suggestedShape,
                `${instanceId}_collision_${part.name}`
            );
            collisionMesh.parent = rootNode;
            
            // Position relative to root - use local position from world bounds
            collisionMesh.position = scaledBounds.center.clone();
            
            try {
                const aggregate = new PhysicsAggregate(
                    collisionMesh,
                    part.suggestedShape,
                    {
                        mass: 0,
                        friction: collisionConfig.friction ?? 0.5,
                        restitution: collisionConfig.restitution ?? 0.1,
                    },
                    this._scene
                );
                
                if (isMain) {
                    result.primary = aggregate;
                } else {
                    result.compound.push(aggregate);
                }
                
                console.log(`MapAssetManager: Created ${isMain ? 'PRIMARY' : 'compound'} collision for "${part.name}" with shape ${PhysicsShapeType[part.suggestedShape]}`);
            } catch (error) {
                console.warn(`MapAssetManager: Failed to create physics for part "${part.name}":`, error);
            }
        }
        
        // If no main was designated, use first compound as primary
        if (!result.primary && result.compound.length > 0) {
            result.primary = result.compound.shift()!;
        }
        
        return result;
    }
    
    /**
     * Create debug visualization for collision shapes
     * Uses mesh wireframes for clearer visualization of actual collision geometry
     */
    private _createDebugVisualization(
        rootNode: TransformNode,
        meshes: AbstractMesh[],
        bounds: ComputedBounds,
        definition: MapAssetDefinition,
        scale: number,
        instanceId: string
    ): Mesh[] {
        const debugMeshes: Mesh[] = [];
        const collisionConfig = definition.collision;
        
        if (!collisionConfig) return debugMeshes;
        
        // Colors for different debug elements
        const meshWireframeColor = new Color3(0, 1, 0.5); // Cyan-green for mesh wireframes
        const collisionShapeColor = new Color3(1, 0.5, 0); // Orange for collision shape
        
        // Create wireframe visualization of actual meshes (clearer than bounding boxes)
        const wireframeMeshes = createMeshWireframeDebug(
            this._scene,
            meshes,
            meshWireframeColor,
            undefined, // Don't parent - they're already positioned correctly
            `${instanceId}_wireframe`
        );
        debugMeshes.push(...wireframeMeshes);
        
        // For CUSTOM collision mode, also show the simplified collision shape
        if (collisionConfig.mode === CollisionMode.CUSTOM && collisionConfig.customDimensions) {
            const custom = collisionConfig.customDimensions;
            const customBounds: ComputedBounds = {
                min: Vector3.Zero(),
                max: Vector3.Zero(),
                center: Vector3.Zero(),
                size: new Vector3(
                    (custom.width ?? custom.radius ?? 0.5) * 2 * scale,
                    (custom.height ?? 1) * scale,
                    (custom.depth ?? custom.radius ?? 0.5) * 2 * scale
                ),
                halfExtents: new Vector3(
                    (custom.width ?? custom.radius ?? 0.5) * scale,
                    (custom.height ?? 1) * scale / 2,
                    (custom.depth ?? custom.radius ?? 0.5) * scale
                ),
                sphereRadius: (custom.radius ?? 0.5) * scale,
                capsuleRadius: (custom.radius ?? 0.5) * scale,
                capsuleHeight: (custom.height ?? 1) * scale,
            };
            
            const collisionDebug = createDebugCollisionMesh(
                this._scene,
                customBounds,
                custom.shapeType,
                collisionShapeColor,
                `${instanceId}_collision_shape`
            );
            collisionDebug.parent = rootNode;
            
            // Position using custom offset if specified
            if (custom.offset) {
                collisionDebug.position = new Vector3(
                    custom.offset.x * scale,
                    custom.offset.y * scale,
                    custom.offset.z * scale
                );
            } else {
                collisionDebug.position = new Vector3(0, customBounds.halfExtents.y, 0);
            }
            
            debugMeshes.push(collisionDebug);
        } else if (collisionConfig.mode !== CollisionMode.MESH && collisionConfig.mode !== CollisionMode.NONE) {
            // For simplified collision modes, show the collision shape in orange
            const scaledBounds: ComputedBounds = {
                ...bounds,
                size: bounds.size.scale(scale),
                halfExtents: bounds.halfExtents.scale(scale),
                center: bounds.center.scale(scale),
                sphereRadius: bounds.sphereRadius * scale,
                capsuleRadius: bounds.capsuleRadius * scale,
                capsuleHeight: bounds.capsuleHeight * scale,
            };
            
            const shapeType = collisionModeToPhysicsShape(collisionConfig.mode);
            const collisionDebug = createDebugCollisionMesh(
                this._scene,
                scaledBounds,
                shapeType,
                collisionShapeColor,
                `${instanceId}_collision_shape`
            );
            collisionDebug.parent = rootNode;
            collisionDebug.position = new Vector3(0, scaledBounds.halfExtents.y, 0);
            
            debugMeshes.push(collisionDebug);
        }
        
        // Set visibility based on debug mode
        const isVisible = this._debugMode || definition.collision?.debugVisualize;
        debugMeshes.forEach(mesh => mesh.setEnabled(isVisible ?? false));
        
        return debugMeshes;
    }

    /**
     * Create collision mesh with custom dimensions
     */
    private _createCustomCollisionMesh(
        custom: NonNullable<MapAssetDefinition["collision"]>["customDimensions"],
        scale: number,
        name: string
    ): Mesh {
        if (!custom) {
            return MeshBuilder.CreateBox(name, { size: 1 }, this._scene);
        }

        let mesh: Mesh;
        
        switch (custom.shapeType) {
            case PhysicsShapeType.BOX:
                mesh = MeshBuilder.CreateBox(`${name}_collision`, {
                    width: (custom.width ?? 1) * scale,
                    height: (custom.height ?? 1) * scale,
                    depth: (custom.depth ?? 1) * scale,
                }, this._scene);
                break;

            case PhysicsShapeType.SPHERE:
                mesh = MeshBuilder.CreateSphere(`${name}_collision`, {
                    diameter: (custom.radius ?? 0.5) * 2 * scale,
                }, this._scene);
                break;

            case PhysicsShapeType.CAPSULE:
                mesh = MeshBuilder.CreateCapsule(`${name}_collision`, {
                    radius: (custom.radius ?? 0.5) * scale,
                    height: (custom.height ?? 2) * scale,
                }, this._scene);
                break;

            case PhysicsShapeType.CYLINDER:
                mesh = MeshBuilder.CreateCylinder(`${name}_collision`, {
                    diameter: (custom.radius ?? 0.5) * 2 * scale,
                    height: (custom.height ?? 2) * scale,
                }, this._scene);
                break;

            default:
                mesh = MeshBuilder.CreateBox(`${name}_collision`, {
                    width: (custom.width ?? 1) * scale,
                    height: (custom.height ?? 1) * scale,
                    depth: (custom.depth ?? 1) * scale,
                }, this._scene);
        }

        // Apply offset if specified
        if (custom.offset) {
            mesh.position = new Vector3(
                custom.offset.x * scale,
                custom.offset.y * scale,
                custom.offset.z * scale
            );
        }

        mesh.isVisible = false;
        return mesh;
    }

    /**
     * Place multiple assets in a batch (more efficient)
     */
    public async placeBatch(config: MapAssetBatchConfig): Promise<MapAssetInstance[]> {
        // Preload the asset first
        await this.preloadAsset(config.assetId);

        const instances: MapAssetInstance[] = [];

        for (const position of config.positions) {
            let finalPosition = position.clone();

            // Adjust to terrain height if enabled
            if (config.adjustToTerrain && this._terrain) {
                const terrainHeight = this._terrain.getHeightAtPosition(
                    position.x,
                    position.z
                );
                finalPosition.y = terrainHeight;
            }

            // Calculate random scale if range specified
            let scale: number | undefined;
            if (config.scaleRange) {
                const [min, max] = config.scaleRange;
                scale = min + Math.random() * (max - min);
            }

            const instance = await this.placeAsset({
                assetId: config.assetId,
                position: finalPosition,
                randomYRotation: config.randomizeRotation ?? true,
                scale,
            });

            instances.push(instance);
        }

        console.log(`MapAssetManager: Placed ${instances.length} instances of "${config.assetId}"`);
        return instances;
    }

    /**
     * Place assets randomly within an area
     */
    public async placeRandom(
        assetId: string,
        count: number,
        area: { minX: number; maxX: number; minZ: number; maxZ: number },
        options?: {
            scaleRange?: [number, number];
            adjustToTerrain?: boolean;
            minDistance?: number;
        }
    ): Promise<MapAssetInstance[]> {
        const positions: Vector3[] = [];
        const minDistance = options?.minDistance ?? 0;
        const maxAttempts = count * 10;
        let attempts = 0;

        while (positions.length < count && attempts < maxAttempts) {
            attempts++;
            
            const x = area.minX + Math.random() * (area.maxX - area.minX);
            const z = area.minZ + Math.random() * (area.maxZ - area.minZ);
            
            // Check minimum distance from other positions
            if (minDistance > 0) {
                const tooClose = positions.some(p => {
                    const dx = p.x - x;
                    const dz = p.z - z;
                    return Math.sqrt(dx * dx + dz * dz) < minDistance;
                });
                if (tooClose) continue;
            }

            positions.push(new Vector3(x, 0, z));
        }

        return this.placeBatch({
            assetId,
            positions,
            randomizeRotation: true,
            scaleRange: options?.scaleRange,
            adjustToTerrain: options?.adjustToTerrain ?? true,
        });
    }

    /**
     * Get an instance by ID
     */
    public getInstance(instanceId: string): MapAssetInstance | undefined {
        return this._instances.get(instanceId);
    }

    /**
     * Get all instances of a specific asset
     */
    public getInstancesByAsset(assetId: string): MapAssetInstance[] {
        return Array.from(this._instances.values())
            .filter(inst => inst.assetId === assetId);
    }

    /**
     * Remove an instance
     */
    public removeInstance(instanceId: string): void {
        const instance = this._instances.get(instanceId);
        if (!instance) return;

        // Dispose primary physics
        if (instance.physicsAggregate) {
            instance.physicsAggregate.dispose();
        }
        
        // Dispose compound physics aggregates
        if (instance.compoundAggregates) {
            instance.compoundAggregates.forEach(agg => agg.dispose());
        }
        
        // Dispose debug meshes
        if (instance.debugMeshes) {
            instance.debugMeshes.forEach(mesh => mesh.dispose());
        }

        // Dispose meshes
        instance.meshes.forEach(mesh => mesh.dispose());

        // Dispose root node
        instance.rootNode.dispose();

        this._instances.delete(instanceId);
        this._emitEvent("instance_destroyed", { instanceId });
    }

    /**
     * Remove all instances of a specific asset
     */
    public removeAssetInstances(assetId: string): void {
        const instanceIds = Array.from(this._instances.entries())
            .filter(([_, inst]) => inst.assetId === assetId)
            .map(([id, _]) => id);

        instanceIds.forEach(id => this.removeInstance(id));
    }

    /**
     * Enable/disable an instance
     */
    public setInstanceEnabled(instanceId: string, enabled: boolean): void {
        const instance = this._instances.get(instanceId);
        if (!instance) return;

        instance.enabled = enabled;
        instance.rootNode.setEnabled(enabled);
        instance.meshes.forEach(m => m.setEnabled(enabled));
        
        // Also toggle debug meshes if in debug mode
        if (instance.debugMeshes && this._debugMode) {
            instance.debugMeshes.forEach(m => m.setEnabled(enabled));
        }
    }
    
    /**
     * Toggle debug visualization for a specific instance
     */
    public setInstanceDebugVisible(instanceId: string, visible: boolean): void {
        const instance = this._instances.get(instanceId);
        if (!instance || !instance.debugMeshes) return;
        
        instance.debugMeshes.forEach(mesh => mesh.setEnabled(visible));
    }

    /**
     * Add shadow casters to a shadow generator
     */
    public addShadowCasters(
        shadowGenerator: import("@babylonjs/core/Lights/Shadows/shadowGenerator").ShadowGenerator,
        assetId?: string
    ): void {
        const instances = assetId 
            ? this.getInstancesByAsset(assetId)
            : Array.from(this._instances.values());

        for (const instance of instances) {
            for (const mesh of instance.meshes) {
                if (mesh.getTotalVertices() > 0) {
                    shadowGenerator.addShadowCaster(mesh);
                }
            }
        }
    }

    /**
     * Subscribe to events
     */
    public on(event: MapAssetEventType, handler: MapAssetEventHandler): void {
        if (!this._eventHandlers.has(event)) {
            this._eventHandlers.set(event, new Set());
        }
        this._eventHandlers.get(event)!.add(handler);
    }

    /**
     * Unsubscribe from events
     */
    public off(event: MapAssetEventType, handler: MapAssetEventHandler): void {
        this._eventHandlers.get(event)?.delete(handler);
    }

    /**
     * Emit an event
     */
    private _emitEvent(type: MapAssetEventType, data?: Record<string, unknown>): void {
        const event: MapAssetEvent = {
            type,
            assetId: data?.assetId as string,
            instanceId: data?.instanceId as string,
            data,
            timestamp: Date.now(),
        };

        this._eventHandlers.get(type)?.forEach(handler => handler(event));
    }

    /**
     * Get asset definition by ID
     */
    public getAssetDefinition(assetId: string): MapAssetDefinition | undefined {
        return this._assetDefinitions.get(assetId);
    }

    /**
     * Get computed bounds for an asset
     */
    public async getAssetBounds(assetId: string): Promise<ComputedBounds | null> {
        await this.preloadAsset(assetId);
        return this._assetCache.get(assetId)?.bounds ?? null;
    }

    /**
     * Dispose all resources
     */
    public dispose(): void {
        // Remove all instances
        for (const instanceId of Array.from(this._instances.keys())) {
            this.removeInstance(instanceId);
        }

        // Dispose cached template meshes
        for (const cached of this._assetCache.values()) {
            cached.meshes.forEach(mesh => mesh.dispose());
        }

        this._assetCache.clear();
        this._assetDefinitions.clear();
        this._eventHandlers.clear();

        console.log("MapAssetManager: Disposed");
    }
}
